import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { BaseService } from '../../core/common/services/base.abstract.service';
import { CustomHttpParamEncoder } from '../../core/http/entities/custom-http-param-encoder';
import { ShAbstractHttpService } from '../../core/http/services/http.abstract.service';
import { OrganisationId } from '../../core/organisations/entities/organisation.entity';
import { MediaHavenPlugin, PluginObject } from '../entities/mediahaven-plugins.entity';

@Injectable({ providedIn: 'root' })
export class ShModulesService extends BaseService {
    private readonly activePluginCache: Map<OrganisationId, Map<MediaHavenPlugin, boolean>> = new Map();

    private readonly httpService: ShAbstractHttpService = inject(ShAbstractHttpService);

    private readonly MODULES_ENDPOINT: string = `v2/modules`;
    private readonly PLUGINS_ENDPOINT: string = `v2/module-plugins`;

    private getCachedValue(pluginId: MediaHavenPlugin, organisationId: OrganisationId): boolean | undefined {
        const organisationCache: Map<MediaHavenPlugin, boolean> | undefined =
            this.activePluginCache.get(organisationId);

        if (organisationCache === undefined) {
            return undefined;
        }

        const cachedValue: boolean | undefined = organisationCache.get(pluginId);
        return cachedValue;
    }

    private setCachedValue(pluginId: MediaHavenPlugin, organisationId: OrganisationId, value: boolean): void {
        if (this.activePluginCache.get(organisationId) === undefined) {
            this.activePluginCache.set(organisationId, new Map());
        }

        const organisationCache: Map<MediaHavenPlugin, boolean> | undefined =
            this.activePluginCache.get(organisationId);

        organisationCache?.set(pluginId, value);
    }

    public getPlugin(pluginId: MediaHavenPlugin): Observable<PluginObject> {
        return this.httpService.get<PluginObject>(`${this.PLUGINS_ENDPOINT}/${pluginId}`);
    }

    public isActivePlugin(pluginId: MediaHavenPlugin, organisationId: OrganisationId): Observable<boolean> {
        const cachedValue: boolean | undefined = this.getCachedValue(pluginId, organisationId);

        if (cachedValue !== undefined) {
            return of(cachedValue);
        }

        const activeEndpoint: string = `${this.MODULES_ENDPOINT}/active`;
        const params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() })
            .set('pluginId', pluginId)
            .set('organisationId', organisationId);

        return this.httpService.head(activeEndpoint, params).pipe(
            map(response => Number(response.headers.get('Result-Count'))),
            map(value => value > 0),
            tap(value => this.setCachedValue(pluginId, organisationId, value)),
            catchError(() => {
                this.setCachedValue(pluginId, organisationId, false);
                return of(false);
            })
        );
    }
}
